import React, { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { PublicLayout } from '../../design-system/components/layouts';
export default function Privacy() {
  const { props } = usePage();
  const companyName = props.app_config?.company_name ?? 'Speakflow';
  const supportEmail = props.app_config?.support_email ?? 'team@speakflow.com';
  const [activeSection, setActiveSection] = useState('');
  const sections = [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      content: (
        <>
          <p>
            When you participate in the {companyName} Affiliate Program, we collect the following
            types of information:
          </p>
          <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">
            Information You Provide
          </h3>
          <ul>
            <li>Name and email address from your application</li>
            <li>PayPal email address for commission payments</li>
            <li>Responses to application questions</li>
            <li>Your chosen affiliate slug and coupon code</li>
          </ul>
          <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">
            Information Collected Automatically
          </h3>
          <ul>
            <li>Referral click data (IP addresses, referrer URLs, UTM parameters)</li>
            <li>Browser and device information from referral clicks</li>
            <li>Commission and referral transaction records</li>
          </ul>
        </>
      ),
    },
    {
      id: 'how-we-use',
      title: '2. How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Operate and manage the Affiliate Program</li>
            <li>Track referrals and calculate commissions</li>
            <li>Process commission payments</li>
            <li>Detect and prevent fraud</li>
            <li>Communicate with you about the Program</li>
            <li>Improve our services and user experience</li>
          </ul>
        </>
      ),
    },
    {
      id: 'fraud-detection',
      title: '3. Fraud Detection',
      content: (
        <>
          <p>
            To protect the integrity of the Program, we employ automated fraud detection measures.
            This may include:
          </p>
          <ul>
            <li>Monitoring referral patterns for suspicious activity</li>
            <li>Checking email addresses against known disposable email providers</li>
            <li>Analyzing IP addresses and click patterns</li>
            <li>Reviewing referral velocity and conversion rates</li>
          </ul>
          <p>Flagged activity is reviewed manually before any action is taken on your account.</p>
        </>
      ),
    },
    {
      id: 'data-sharing',
      title: '4. Data Sharing',
      content: (
        <>
          <p>We do not sell your personal information. We may share your information with:</p>
          <ul>
            <li>
              <strong>Payment processors</strong> (PayPal) to facilitate commission payments
            </li>
            <li>
              <strong>Stripe</strong> for referral attribution and subscription tracking
            </li>
            <li>
              <strong>Service providers</strong> who assist in operating the Program (hosting,
              analytics)
            </li>
            <li>
              <strong>Legal authorities</strong> when required by law or to protect our rights
            </li>
          </ul>
        </>
      ),
    },
    {
      id: 'data-retention',
      title: '5. Data Retention',
      content: (
        <>
          <p>
            We retain your information for as long as your affiliate account is active and as needed
            to fulfill our obligations under the Program.
          </p>
          <p>
            After account termination, we may retain certain data for a reasonable period to comply
            with legal obligations, resolve disputes, and enforce agreements.
          </p>
          <p>
            Referral click data is retained for attribution purposes and may be anonymized after 24
            months.
          </p>
        </>
      ),
    },
    {
      id: 'cookies',
      title: '6. Cookies and Tracking',
      content: (
        <>
          <p>
            The {companyName} Affiliate Program uses cookies to track referrals. When a visitor
            clicks your referral link, a cookie is placed to attribute any subsequent subscription
            to your account.
          </p>
          <p>
            We also use standard web analytics tools to understand traffic patterns and improve the
            platform.
          </p>
        </>
      ),
    },
    {
      id: 'your-rights',
      title: '7. Your Rights',
      content: (
        <>
          <p>
            Depending on your location, you may have the following rights regarding your personal
            information:
          </p>
          <ul>
            <li>
              <strong>Access</strong> — request a copy of the data we hold about you
            </li>
            <li>
              <strong>Correction</strong> — request that we correct inaccurate data
            </li>
            <li>
              <strong>Deletion</strong> — request that we delete your personal data
            </li>
            <li>
              <strong>Portability</strong> — request your data in a portable format
            </li>
            <li>
              <strong>Objection</strong> — object to certain types of processing
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a
              href={`mailto:${supportEmail}`}
              className="text-fg hover:text-fg-secondary underline"
            >
              {supportEmail}
            </a>
            .
          </p>
        </>
      ),
    },
    {
      id: 'security',
      title: '8. Security',
      content: (
        <p>
          We implement industry-standard security measures to protect your personal information,
          including encrypted data transmission (TLS/SSL), secure authentication, and access
          controls. However, no method of electronic storage is 100% secure, and we cannot guarantee
          absolute security.
        </p>
      ),
    },
    {
      id: 'changes',
      title: '9. Changes to This Policy',
      content: (
        <p>
          We may update this Privacy Policy from time to time. We will notify affiliates of material
          changes via email or through the affiliate dashboard. Continued participation in the
          Program after changes are posted constitutes acceptance of the updated policy.
        </p>
      ),
    },
    {
      id: 'contact',
      title: '10. Contact Us',
      content: (
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices, please
          contact us at{' '}
          <a
            href={`mailto:${supportEmail}`}
            className="text-fg hover:text-fg-secondary underline"
          >
            {supportEmail}
          </a>
          .
        </p>
      ),
    },
  ];
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      },
    );
    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl font-semibold text-fg tracking-tight">Privacy Policy</h1>
          <p className="mt-2 text-sm text-fg-tertiary">Last updated: February 2026</p>
        </div>

        <div className="flex gap-12">
          {/* Table of contents sidebar */}
          <nav className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-8">
              <h2 className="text-xs font-semibold text-fg-tertiary uppercase tracking-wider mb-3">
                On this page
              </h2>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={`block text-sm py-1 transition-colors ${activeSection === section.id ? 'text-fg font-medium' : 'text-fg-tertiary hover:text-fg'}`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="prose prose-neutral max-w-none">
              <p className="text-fg-secondary leading-relaxed">
                This Privacy Policy describes how {companyName} collects, uses, and protects your
                personal information when you participate in the {companyName} Affiliate Program.
              </p>

              {sections.map((section) => (
                <section key={section.id} id={section.id} className="mt-10">
                  <h2 className="text-lg font-semibold text-fg mb-4">{section.title}</h2>
                  <div className="text-fg-secondary leading-relaxed space-y-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
                    {section.content}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
